import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import {
  KpiBreakpointNotificationMetadata,
  KpiBudgetExceededNotificationMetadata,
  TrackingOverdueNotificationMetadata,
  Notification,
  NotificationContentType,
  ProjectDeadlineExceededNotificationMetadata,
  ProjectEndHintNotificationMetadata,
  ProjectMemberNotificationMetadata,
  TrackingRequestReceivedNotificationMetadata,
  TrackingRequestSentNotificationMetadata,
  ContractorInvitationAcceptedNotificationMetadata,
  ContractorInvitationNotificationMetadata,
} from '@/features/notifications/notification-types';

export function useNotificationNavigation() {
  const navigate = useNavigate();

  const handleNotificationClick = (notification: Notification) => {
    switch (notification.content_type) {
      case NotificationContentType.new_org_member:
        navigate(paths.org.memberManagement.path);
        break;
      case NotificationContentType.new_project_member:
        const projectMemberMetadata =
          notification.metadata as ProjectMemberNotificationMetadata;
        navigate(
          paths.projects.details.memberManagement.getHref(
            projectMemberMetadata.project_id,
          ),
        );
        break;
      case NotificationContentType.project_end_hint:
        const projectEndHintMetadata =
          notification.metadata as ProjectEndHintNotificationMetadata;
        navigate(
          paths.projects.details.base.getHref(
            projectEndHintMetadata.project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.project_deadline_exceeded:
        const projectDeadlineExceededMetadata =
          notification.metadata as ProjectDeadlineExceededNotificationMetadata;
        navigate(
          paths.projects.details.base.getHref(
            projectDeadlineExceededMetadata.project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.tracking_request_sent:
        const trackingRequestSentMetadata =
          notification.metadata as TrackingRequestSentNotificationMetadata;
        navigate(
          paths.projects.details.trackingReportHistory.getHref(
            trackingRequestSentMetadata.client_project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.tracking_request_received:
        const trackingRequestReceivedMetadata =
          notification.metadata as TrackingRequestReceivedNotificationMetadata;
        navigate(
          paths.projects.details.trackingReportHistory.getHref(
            trackingRequestReceivedMetadata.contractor_subproject_id.toString(),
          ),
        );
        break;
      case NotificationContentType.kpi_breakpoint:
        const kpiBreakpointMetadata =
          notification.metadata as KpiBreakpointNotificationMetadata;
        navigate(
          paths.projects.details.base.getHref(
            kpiBreakpointMetadata.project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.kpi_budget_exceeded:
        const kpiBudgetExceededMetadata =
          notification.metadata as KpiBudgetExceededNotificationMetadata;
        navigate(
          paths.projects.details.base.getHref(
            kpiBudgetExceededMetadata.project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.tracking_overdue:
        const trackingOverdueMetadata =
          notification.metadata as TrackingOverdueNotificationMetadata;
        navigate(
          paths.projects.details.base.getHref(
            trackingOverdueMetadata.project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.contractor_invitation_accepted:
        const contractorInvitationAcceptedMetadata =
          notification.metadata as ContractorInvitationAcceptedNotificationMetadata;
        navigate(
          paths.projects.details.base.getHref(
            contractorInvitationAcceptedMetadata.project_id.toString(),
          ),
        );
        break;
      case NotificationContentType.contractor_invitation:
        const contractorInvitationMetadata =
          notification.metadata as ContractorInvitationNotificationMetadata;
        if (contractorInvitationMetadata.isNewPartner) {
          navigate(
            paths.partners.manageInvitation.getHref(
              contractorInvitationMetadata.invitation_token,
            ),
          );
          break;
        } else {
          navigate(
            paths.partners.acceptContractorSubproject.getHref(
              contractorInvitationMetadata.invitation_token,
            ),
          );
        }
        break;
    }
  };

  return { handleNotificationClick };
}
